import React, { useState, useEffect, useRef } from 'react';

const RideRoutePreview = ({ pickupCoords, destCoords, onRouteLoaded, onImageCapture }) => {
  const canvasRef = useRef(null);
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const capturedRef = useRef(false);

  useEffect(() => {
    if (!pickupCoords?.lat || !pickupCoords?.lng || !destCoords?.lat || !destCoords?.lng) return;
    let cancelled = false;
    setLoading(true);
    capturedRef.current = false;
    const url = `https://router.project-osrm.org/route/v1/driving/${pickupCoords.lng},${pickupCoords.lat};${destCoords.lng},${destCoords.lat}?overview=full&geometries=geojson`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (cancelled || !data.routes?.[0]) return;
        const route = data.routes[0];
        const positions = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        const distMiles = (route.distance * 0.000621371).toFixed(1);
        const durMin = Math.round(route.duration / 60);
        setRouteData({ positions, distMiles, durMin });
        onRouteLoaded?.(distMiles, durMin);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pickupCoords?.lat, pickupCoords?.lng, destCoords?.lat, destCoords?.lng]);

  useEffect(() => {
    if (!routeData || !canvasRef.current || capturedRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const PAD = 50;

    const { positions } = routeData;
    const lats = positions.map(p => p[0]);
    const lngs = positions.map(p => p[1]);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);

    const toTFX = (lng, z) => (lng + 180) / 360 * (1 << z);
    const toTFY = (lat, z) => {
      const r = lat * Math.PI / 180;
      return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2 * (1 << z);
    };

    let zoom = 5;
    for (let z = 17; z >= 5; z--) {
      const dx = (toTFX(maxLng, z) - toTFX(minLng, z)) * 256;
      const dy = (toTFY(minLat, z) - toTFY(maxLat, z)) * 256;
      if (dx <= W - PAD * 2 && dy <= H - PAD * 2) { zoom = z; break; }
    }

    const cTX = (toTFX(minLng, zoom) + toTFX(maxLng, zoom)) / 2;
    const cTY = (toTFY(minLat, zoom) + toTFY(maxLat, zoom)) / 2;

    const project = (lat, lng) => ({
      x: (toTFX(lng, zoom) - cTX) * 256 + W / 2,
      y: (toTFY(lat, zoom) - cTY) * 256 + H / 2,
    });

    const tx0 = Math.floor(cTX - W / 512);
    const tx1 = Math.ceil(cTX + W / 512);
    const ty0 = Math.floor(cTY - H / 512);
    const ty1 = Math.ceil(cTY + H / 512);

    ctx.fillStyle = '#e8e0d0';
    ctx.fillRect(0, 0, W, H);

    const subdomains = ['a', 'b', 'c'];
    const loadTile = (tx, ty) => new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const px = (tx - cTX) * 256 + W / 2;
        const py = (ty - cTY) * 256 + H / 2;
        ctx.drawImage(img, px, py, 256, 256);
        resolve();
      };
      img.onerror = () => resolve();
      const s = subdomains[(Math.abs(tx + ty)) % 3];
      img.src = `https://${s}.tile.openstreetmap.org/${zoom}/${tx}/${ty}.png`;
    });

    const tileJobs = [];
    for (let tx = tx0; tx <= tx1; tx++)
      for (let ty = ty0; ty <= ty1; ty++)
        tileJobs.push(loadTile(tx, ty));

    // Just the route outline — a plain line plus small start/end dots, no
    // pins/labels/info bar. This is a card thumbnail, not the detail view
    // (which is the live RideRouteMap, opened separately on click).
    Promise.all(tileJobs).then(() => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, W, H);
      ctx.clip();

      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 8;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      positions.forEach(([lat, lng], i) => {
        const { x, y } = project(lat, lng);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();

      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 4;
      ctx.beginPath();
      positions.forEach(([lat, lng], i) => {
        const { x, y } = project(lat, lng);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();

      const drawDot = (px, py, fill) => {
        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.arc(px, py, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(px, py, 7, 0, Math.PI * 2);
        ctx.stroke();
      };

      const ps = project(pickupCoords.lat, pickupCoords.lng);
      drawDot(ps.x, ps.y, '#16a34a');
      const pe = project(destCoords.lat, destCoords.lng);
      drawDot(pe.x, pe.y, '#dc2626');

      ctx.restore();

      capturedRef.current = true;
      if (onImageCapture) {
        try {
          canvas.toBlob(blob => { if (blob) onImageCapture(blob); }, 'image/png');
        } catch (_) {}
      }
    });
  }, [routeData]);

  if (loading) return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e8e0d0', flexDirection: 'column', gap: '0.5rem' }}>
      <span style={{ fontSize: '2rem' }}>🗺️</span>
      <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>Loading route…</span>
    </div>
  );
  if (!routeData) return null;

  return <canvas ref={canvasRef} width={400} height={600} style={{ width: '100%', height: '100%', display: 'block', borderRadius: 'inherit' }} />;
};

export default RideRoutePreview;
